import { Banner, Typography } from '@oresundsbron/bridge-ui';
import { Grid, RadioGroup, Input } from '@oresundsbron/bridge-ui';
import { useForm, useSubscribe } from '@oresundsbron/use-form';
import { pipe } from 'fp-ts/lib/function';
import { fromPredicate, map, alt, getOrElseW, match } from 'fp-ts/lib/Option';
import { FC, useEffect, useMemo } from 'react';
import { PaperInvoiceForm } from './PaperInvoice';
import { Data, Disclaimers, Labels, Placeholders } from './types';
import { Data as TermsData } from '../TermsOfAgreement/Business';
import { getDefaultCurrency } from '../../../lib/currency';
import { useRouter } from 'next/router';

export const InvoiceForm: FC<{
  labels: Labels;
  namespace: string;
  countryOptions: { key: string; value: string }[];
  placeholders: Placeholders;
  disclaimers: Disclaimers;
}> = ({ labels, namespace, countryOptions, placeholders, disclaimers }) => {
  const { locale } = useRouter();
  const { registerField, getError, isActive } = useForm<Data>({
    namespace,
  });

  const { transformData } = useForm<TermsData>({
    namespace: 'termsOfAgreement',
  });

  const [sendToEmail, displayInEuro] = useSubscribe<Data>(
    namespace,
    'invoice.sendToEmail',
    'invoice.displayInEuro'
  );

  const content = useMemo(
    () =>
      pipe(
        sendToEmail,
        fromPredicate((x) => x === 'true'),
        map(() => (
          <Input
            {...registerField('invoice.email')}
            className="col-span-full sm:col-span-4 md:col-span-6"
            type="email"
            error={getError('invoice.email')}
            required
            label={labels.email}
          />
        )),
        alt(() =>
          pipe(
            sendToEmail,
            fromPredicate((x) => x === 'false'),
            map(() => (
              <>
                <Banner className="col-span-full">
                  <Typography size="sm">{disclaimers.paper}</Typography>
                </Banner>
                <PaperInvoiceForm
                  namespace={namespace}
                  labels={labels}
                  countryOptions={countryOptions}
                  placeholders={placeholders}
                />
              </>
            ))
          )
        ),
        getOrElseW(() => null)
      ),
    [
      labels,
      registerField,
      sendToEmail,
      namespace,
      countryOptions,
      placeholders,
      getError,
      disclaimers,
    ]
  );

  useEffect(
    () =>
      pipe(
        displayInEuro,
        fromPredicate((x) => x === 'true'),
        match(
          () => {
            transformData((state) => ({
              ...state,
              currency: getDefaultCurrency(locale || ''),
            }));
          },
          () => {
            transformData((state) => ({ ...state, currency: 'DKK' }));
          }
        )
      ),
    // eslint-disable-next-line
    [displayInEuro, locale]
  );

  return (
    <Grid>
      <Banner className="col-span-full">
        <Typography size="sm">{disclaimers.invoice}</Typography>
      </Banner>
      <RadioGroup.Root
        {...registerField('invoice.sendToEmail')}
        label={labels.invoiceDelivery}
        required
        className="col-span-full"
      >
        <RadioGroup.Item
          label={labels.sendToEmail.true}
          value="true"
          disabled={!isActive}
        />
        <RadioGroup.Item
          label={labels.sendToEmail.false}
          value="false"
          disabled={!isActive}
        />
      </RadioGroup.Root>
      <Input
        className="col-span-full"
        {...registerField('invoice.displayInEuro', {
          type: 'checkbox',
          value: 'true',
        })}
        label={labels.displayInEuro}
        error={getError('invoice.displayInEuro')}
        disabled={!isActive}
      />

      {content}
    </Grid>
  );
};
